import { ForwardButton } from "~/assets/js/components/Button";
import React, { useContext, useState, useEffect } from "react";
import Popup from "~/assets/js/components/Popup";
import { SkeletonTimeSlots } from "~/assets/js/components/Loaders/SkeletonLoader";
import StickyFooter from "~/assets/js/components/StickyFooter";
import Timeslot from "~/assets/js/components/Timeslot";
import {
    getDeliveryTimes,
    getHasExpiredSlots,
} from "~/assets/js/Api/deliveryTimesApi";
import isEmpty from "lodash.isempty";
import useViewport from "~/assets/js/Helpers/hooks/useViewport";
import CheckoutSummary from "~/assets/js/components/CheckoutSummary";
import Headline from "~/assets/js/components/Typography/Headline";
import useAdditionalUpdateEffect from "~/assets/js/Helpers/hooks/useAdditionalUpdateEffect";
import { unpackAreaCookie } from "~/assets/js/Format";
import useAreaCookie from "~/assets/js/Helpers/hooks/useAreaCookie";
import useLocationEventListener from "~/assets/js/Helpers/hooks/useLocationEventListener";
import moment from "moment";

import TimeSlotSelectInfo from "./TimeSlotSelectInfo";
import SelectDaySlot from "./SelectDaySlot";
import EarlyOnLocationToggle from "./EarlyOnLocationToggle";
import { formatDate } from "./formatDateTime";
import {
    CheckoutContext,
    ACTIONS,
    PAGES,
} from "~/assets/js/pages/Checkout/CheckoutProvider";
import { DesktopGrid } from "~/assets/js/components/Layout/DesktopGrid";
import Notification from "~/assets/js/components/Notification";
import { getFirstAvailableTimeslot } from "~/assets/js/Helpers/timeslot-utils";
import { useCartContext } from "~/assets/js/global/cartContext";
import { CART_ERRORS_MAP } from "../../../../global/cartErrorsHandler";
import { DELIVERY_TYPES } from "../../../../components/Topbar";
import { AddToast } from "../../../../components/Toast";
import i18next from "i18next";

const NOTIFICATION = {
    ALL_SLOTS_TAKEN: "timeSlotsTaken",
    NO_SLOTS_TODAY: "noSlotsToday",
    STORE_CLOSED: "storeClosed",
    SLOT_FULL_PICKUP: "slotFullPickup",
    SLOT_FULL_DELIVERY: "slotFullDelivery",
    SLOT_TIMEOUT_PICKUP: "slotTimeoutPickup",
    SLOT_TIMEOUT_DELIVERY: "slotTimeoutDelivery",
};

const TimeSlotSelect = () => {
    const { cart, actions } = useCartContext();
    const cookie = useAreaCookie();
    const cookieContent = unpackAreaCookie(cookie);
    const { state: contextState, dispatch } = useContext(CheckoutContext);
    const { isDesktop } = useViewport();
    const isDelivery = cookie.area.type === "delivery";
    const [state, setState] = useState({
        isLoading: false,
        groupedDeliveryTimes: {},
        selectedDay: [],
        firstAvailable: {},
        selectedTime: contextState.timeSlot,
        timeSelectOpen: false,
        earlyOnLocationOpen: false,
        timeSelectInfoOpen: false,
        notification: null,
    });

    useLocationEventListener(() => {
        dispatch({
            type: ACTIONS.CHANGE_PAGE,
            payload: PAGES.PAYMENT_SELECT,
        });
    });

    useEffect(() => {
        const hasExpiredSlots = getHasExpiredSlots();
        const storeClosed = checkStoreClosed();
        const slotNotification = getSlotNotification(hasExpiredSlots);

        if (storeClosed) {
            setState({ ...state, notification: NOTIFICATION.STORE_CLOSED });
            return;
        }

        if (slotNotification) {
            setState({
                ...state,
                notification: slotNotification,
            });
            return;
        }

        setState({ ...state, notification: null });
    }, [state.groupedDeliveryTimes, cart]);

    const fetchDeliveryTimes = async (selectedDeliveryDate) => {
        setState({ ...state, isLoading: true });
        const deliveryTimes = await getDeliveryTimes();
        let currentDay = [];

        if (selectedDeliveryDate) {
            currentDay = deliveryTimes[selectedDeliveryDate];
        }

        setState({
            ...state,
            groupedDeliveryTimes: deliveryTimes,
            selectedDay: currentDay,
            isLoading: false,
        });
    };

    const getSlotNotification = (hasExpiredSlots) => {
        const today = moment().format("YYYY-MM-DD");
        const firstAvailableDay = Object.keys(state.groupedDeliveryTimes)[0];

        let freeSlotsToday = false;
        let slotsToday = 0;
        if (today === firstAvailableDay) {
            state.groupedDeliveryTimes[firstAvailableDay].map((timeSlot) => {
                slotsToday += 1;
                if (timeSlot.free) {
                    freeSlotsToday = true;
                }
            });
        }

        // CART ERROR NOTIFICATIONS
        if (
            cart.errors &&
            cart.errors.includes(CART_ERRORS_MAP.SLOT_FULL) &&
            !freeSlotsToday
        )
            return cart.dispatch_type === DELIVERY_TYPES.PICKUP
                ? NOTIFICATION.SLOT_FULL_PICKUP
                : NOTIFICATION.SLOT_FULL_DELIVERY;

        if (
            cart.errors &&
            cart.errors.includes(CART_ERRORS_MAP.SLOT_TIMEOUT) &&
            !freeSlotsToday
        )
            return cart.dispatch_type === DELIVERY_TYPES.PICKUP
                ? NOTIFICATION.SLOT_TIMEOUT_PICKUP
                : NOTIFICATION.SLOT_TIMEOUT_DELIVERY;

        // CURRENT STATE NOTIFICATIONS
        // if there are no expired slots and no available slots we don't have slot's at all
        if (!slotsToday || !hasExpiredSlots) return NOTIFICATION.NO_SLOTS_TODAY;
        if (!freeSlotsToday) return NOTIFICATION.ALL_SLOTS_TAKEN;
    };

    //remove current day if store is closed or no available timeSlots for today
    const removeFirstDay = (groupedDeliveryTimes) => {
        const today = moment().format("YYYY-MM-DD");
        const firstDay = Object.keys(groupedDeliveryTimes)[0];

        if (firstDay === today && state.notification) {
            delete groupedDeliveryTimes[firstDay];
            return groupedDeliveryTimes;
        }
        return groupedDeliveryTimes;
    };

    const checkStoreClosed = () => {
        //temporary solution for store closing at 20:41
        const currentTime = moment();
        const startOfDay = currentTime.clone().startOf("day");
        const timeSinceMidnight = currentTime.diff(startOfDay, "seconds");
        const closingTime = 74460;

        if (timeSinceMidnight > closingTime) {
            return true;
        }
        return false;
    };

    const clearSelection = () => {
        dispatch({
            type: ACTIONS.ADD_TIME_SLOT,
            payload: {},
        });
        setState({ selectedTime: {} });
    };

    useAdditionalUpdateEffect(
        () => fetchDeliveryTimes(contextState?.timeSlot?.deliveryDay),
        cookie,
        clearSelection
    );

    const onTimeSlotSelect = (timeSlot) => {
        setState({ ...state, selectedTime: timeSlot });
    };

    const applyTimeSlot = async (timeSlot) => {
        setState({ ...state, isLoading: true });

        dispatch({
            type: ACTIONS.ADD_TIME_SLOT,
            payload: timeSlot,
        });

        const selectedDay = state.groupedDeliveryTimes[timeSlot.deliveryDay];

        setState({
            ...state,
            timeSelectOpen: false,
            isLoading: false,
            selectedDay: selectedDay,
            earlyOnLocationOpen: cookieContent.isDelivery,
        });
    };

    const openPopup = (selectedDay) => {
        setState({
            ...state,
            timeSelectOpen: true,
            selectedDay: selectedDay,
            firstAvailable: getFirstAvailableTimeslot(selectedDay),
        });
    };

    const openInfo = () => {
        setState({ ...state, timeSelectInfoOpen: true });
    };

    const closeInfo = () => {
        setState({ ...state, timeSelectInfoOpen: false });
    };

    const timeSelectClose = () => {
        setState({ ...state, timeSelectOpen: false });
    };

    const EarlyOnLocationClose = () => {
        setState({ ...state, earlyOnLocationOpen: false });
    };

    const goToNextPage = async () => {
        const { timeSlot } = contextState;
        const response = await actions.updateTimeSlot(timeSlot);

        if (response.ok && response.cart.valid === "valid") {
            dispatch({
                type: ACTIONS.CHANGE_PAGE,
                payload: PAGES.ORDER_OVERVIEW,
            });
            return;
        }

        dispatch(AddToast("danger", i18next.t("toasts.timeslots.error")));
    };

    const popupConfig = {
        title: cookieContent.isDelivery
            ? i18next.t("timeSlotSelect.deliveryTitle")
            : i18next.t("timeSlotSelect.pickupTitle"),
        subtitle:
            state.selectedDay && state.selectedDay[0]
                ? `${
                      formatDate(state.selectedDay[0].deliveryDay).append
                  }, den ${formatDate(state.selectedDay[0].deliveryDay).date}`
                : "",
        handleClose: timeSelectClose,
        handleInfo: openInfo,
        open: state.timeSelectOpen,
        isClosable: true,
        isInfo: true,
        position: isDesktop ? "side" : "bottom",
    };

    const hasSelectedTimeslot =
        !!contextState?.timeSlot &&
        Object.keys(contextState.timeSlot).length > 0;

    return (
        <>
            <DesktopGrid
                leftSide={
                    state.isLoading ? (
                        <SkeletonTimeSlots />
                    ) : (
                        <div className="day-select__container">
                            <Popup {...popupConfig}>
                                <div className="timeslot__container">
                                    <TimeSlotSelectInfo
                                        open={state.timeSelectInfoOpen}
                                        handleClose={closeInfo}
                                    />

                                    {!!state.firstAvailable && (
                                        <Timeslot
                                            isFirst={true}
                                            isFirstContent={
                                                isDelivery
                                                    ? i18next.t(
                                                          "timeSlotSelect.earliestDelivery"
                                                      )
                                                    : i18next.t(
                                                          "timeSlotSelect.earliestPickup"
                                                      )
                                            }
                                            time={state.firstAvailable}
                                            availability={
                                                state.firstAvailable.capacity
                                            }
                                            onValueChange={() =>
                                                onTimeSlotSelect(
                                                    state.firstAvailable
                                                )
                                            }
                                            defaultValue={state.selectedTime}
                                        />
                                    )}

                                    {!!(
                                        state.selectedDay &&
                                        state.selectedDay.length
                                    ) &&
                                        state.selectedDay.map((timeSlot, i) => {
                                            return (
                                                <Timeslot
                                                    time={timeSlot}
                                                    availability={
                                                        timeSlot.capacity
                                                    }
                                                    onValueChange={() =>
                                                        onTimeSlotSelect(
                                                            timeSlot
                                                        )
                                                    }
                                                    key={i}
                                                    defaultValue={
                                                        state.selectedTime
                                                    }
                                                />
                                            );
                                        })}
                                </div>
                                <div className="timeslot_button__container">
                                    <ForwardButton
                                        label={i18next.t("cart.continue")}
                                        size="SM"
                                        customStyle="timeslot_button"
                                        onClick={() => {
                                            applyTimeSlot(state.selectedTime);
                                        }}
                                        loading={state.isLoading}
                                        variant="secondary"
                                        disabled={isEmpty(state.selectedTime)}
                                    />
                                </div>
                            </Popup>
                            <EarlyOnLocationToggle
                                open={state.earlyOnLocationOpen}
                                handleClose={EarlyOnLocationClose}
                            />
                            <Headline
                                size={5}
                                resizable
                                className="day-select__header"
                            >
                                <TimeSlotSelectHeader />
                            </Headline>
                            {!!state.notification && (
                                <Notification
                                    style={{
                                        margin: "20px 0",
                                        display: "flex",
                                    }}
                                    type="danger"
                                >
                                    <Headline size={6}>
                                        {i18next.t(
                                            `timeSlotSelect.${state.notification}.heading`
                                        )}
                                    </Headline>
                                    <p
                                        style={{ margin: 0 }}
                                        dangerouslySetInnerHTML={{
                                            __html: i18next.t(
                                                `timeSlotSelect.${state.notification}.content`
                                            ),
                                        }}
                                    ></p>
                                </Notification>
                            )}
                            <SelectDaySlot
                                groupedDeliveryTimes={removeFirstDay(
                                    state.groupedDeliveryTimes
                                )}
                                openTimeSelectPopup={openPopup}
                            />
                        </div>
                    )
                }
                rightSide={
                    isDesktop && (
                        <CheckoutSummary
                            handleDesktopMoveForward={goToNextPage}
                            forwardButtonDisabled={!hasSelectedTimeslot}
                            loadingForwardButton={state.isLoading}
                        />
                    )
                }
            />

            <StickyFooter
                disabled={!hasSelectedTimeslot}
                totals={cart.value_gross}
                onClick={goToNextPage}
            />
        </>
    );
};

const TimeSlotSelectHeader = () => {
    const cookie = useAreaCookie();
    const { iconPath } = reactConfig;
    const icon =
        cookie.area.type === "delivery"
            ? iconPath + "/bicycle-new.svg"
            : iconPath + "/shop-new.svg";
    return (
        <>
            <img src={icon} alt="delivery-icon"></img>
            {i18next.t(`timeSlotSelect.header.${cookie.area.type}`)}
        </>
    );
};

export default TimeSlotSelect;
